import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';

import { RouteReuseStrategy } from '@angular/router';

import { SocialSharing } from '@awesome-cordova-plugins/social-sharing/ngx';
import { IonicModule, IonicRouteStrategy, NavController, ToastController } from '@ionic/angular';

import { FormBuilder } from '@angular/forms';
import { NgHeaderTemplateDirective, NgSelectModule } from '@ng-select/ng-select';

import {
    HTTP_INTERCEPTORS,
    HttpClient,
    provideHttpClient,
    withInterceptorsFromDi,
} from '@angular/common/http';
import { RmsAuthTokenInterceptor } from './interceptors/rms-auth-token.interceptor';
import { RmsAuthRefreshInterceptor } from './interceptors/rms-auth-refresh.interceptor';
import { CmsApiKeyInterceptor } from './interceptors/cms-api-key.interceptor';
import { RmsAuthService } from './services/rms-auth.service';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { AngularFireDatabaseModule } from '@angular/fire/compat/database';
import { AngularFirestoreModule } from '@angular/fire/compat/firestore';
import { AngularFireStorageModule } from '@angular/fire/compat/storage';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { environment } from '@environment';
import { NgOptionHighlightModule } from '@ng-select/ng-option-highlight';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import { provideCacheableAnimationLoader, provideLottieOptions } from 'ngx-lottie';
import { register } from 'swiper/element/bundle';
import { UserState } from '../store/state/currentUser.state';
import { FavoritosState } from '../store/state/favoritar.state';

// Use @sentry/angular-ivy for Angular 12+ or `@sentry/angular` from Angular 10 and 11
// import * as SentryAngular from '@sentry/angular-ivy';

// import { AirbrakeErrorHandler } from '@shared/services/airbrake.service';
register();

/*
Sentry.init(
    {
        dsn: 'https://1a26b09c7c374942ea5d080e267a66be@o4503905791377408.ingest.us.sentry.io/4506962200100864',

        integrations: [
            new SentryAngular.BrowserTracing({
                // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
                tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
                routingInstrumentation: SentryAngular.routingInstrumentation,
            }),
            new SentryAngular.Replay({
                maskAllText: false,
                blockAllMedia: false,
            }),
        ] as any,
        // Performance Monitoring
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    },
    // Forward the init method from @sentry/angular-ivy
    SentryAngular.init
);
*/
export function playerFactory() {
    return import(/* webpackChunkName: 'lottie-web' */ 'lottie-web');
}

@NgModule({
    declarations: [],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        BrowserAnimationsModule,
        IonicModule.forRoot({
            backButtonText: 'Voltar',
            backButtonIcon: 'arrow-back',
            persistConfig: true,
            mode: 'md',
        }),
        AngularFireModule.initializeApp(environment.firebase),
        AngularFireAuthModule,
        AngularFireDatabaseModule,
        AngularFirestoreModule.enablePersistence({ synchronizeTabs: true }),
        AngularFireStorageModule,
        NgSelectModule,
        NgOptionHighlightModule,
        NgxsModule.forRoot([UserState, FavoritosState], {
            developmentMode: !environment.production,
        }),
        NgxsStoragePluginModule.forRoot({
            keys: ['tabelas', 'corretor', 'rms_usuario_current'],
        }),
        NgxsReduxDevtoolsPluginModule.forRoot({
            maxAge: 25, // Retains last 25 states
        }),
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: environment.production,
        }),
        CKEditorModule,
    ],
    exports: [BrowserModule, IonicModule],
    providers: [
        NgHeaderTemplateDirective,
        // {
        //     provide: HTTP_INTERCEPTORS,
        //     useFactory: (
        //         loaderService: LoaderService,
        //         overlayService: OverlayService,
        //         toastCtrl: ToastController,
        //         modalCtrl: ModalController,
        //         navCtrl: NavController,
        //         router: Router,
        //     ) =>
        //         new LoadingWithRetryInterceptor(
        //             loaderService,
        //             overlayService,
        //             toastCtrl,
        //             modalCtrl,
        //             navCtrl,
        //             router,
        //         ),
        //     deps: [
        //         LoaderService,
        //         OverlayService,
        //         ToastController,
        //         ModalController,
        //         NavController,
        //         Router,
        //     ],
        //     multi: true,
        // },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: RmsAuthTokenInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CmsApiKeyInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useFactory: (
                rmsAuth: RmsAuthService,
                nav: NavController,
                toast: ToastController,
                http: HttpClient,
            ) => new RmsAuthRefreshInterceptor(rmsAuth, nav, toast, http),
            deps: [RmsAuthService, NavController, ToastController, HttpClient],
            multi: true,
        },
        {
            provide: RouteReuseStrategy,
            useClass: IonicRouteStrategy,
        },

        // { provide: ErrorHandler, useClass: AirbrakeErrorHandler },
        FormBuilder,
        SocialSharing,
        provideHttpClient(withInterceptorsFromDi()),
        provideLottieOptions({ player: playerFactory }),
        provideCacheableAnimationLoader(),

        /*
        {
            provide: ErrorHandler,
            // Attach the Sentry ErrorHandler
            useValue: SentryAngular.createErrorHandler(),
        },
        {
            provide: SentryAngular.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [SentryAngular.TraceService],
            multi: true,
        },
       */
    ],
})
export class CoreModule {}
